/**
 * WebAccessibilityToggle - Floating accessibility toggle for Customer Website
 * Provides large text and high contrast options
 * Supports Light/Dark mode
 */
import { useState, useEffect } from 'react';
import { Eye, EyeOff, Type, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function WebAccessibilityToggle({
  accessibilityEnabled,
  setAccessibilityEnabled
}) {
  const [showPanel, setShowPanel] = useState(false);

  // Apply accessibility class to document when enabled
  useEffect(() => {
    if (accessibilityEnabled) {
      document.documentElement.classList.add('accessibility-mode');
    } else {
      document.documentElement.classList.remove('accessibility-mode');
    }
  }, [accessibilityEnabled]);

  return (
    <div className="fixed bottom-20 lg:bottom-4 right-4 z-40" data-testid="web-accessibility-toggle">
      {/* Toggle Button */}
      <Button
        onClick={() => setShowPanel(!showPanel)}
        variant="outline"
        className={`rounded-full shadow-lg w-12 h-12 p-0 border-2 transition-all ${
          accessibilityEnabled 
            ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90' 
            : 'bg-card text-foreground border-border hover:bg-muted'
        }`}
        data-testid="web-accessibility-btn"
        aria-label="Accessibility options"
      >
        {accessibilityEnabled ? (
          <Eye className="w-5 h-5" />
        ) : (
          <EyeOff className="w-5 h-5" />
        )}
      </Button>

      {/* Expanded Panel */}
      {showPanel && (
        <div className="absolute bottom-14 right-0 bg-card rounded-xl p-4 shadow-2xl border border-border w-64 animate-in slide-in-from-bottom-2 fade-in duration-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              Accessibility
            </h3>
            <button
              onClick={() => setShowPanel(false)}
              className="p-1 hover:bg-muted rounded-full"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Large Text Toggle */}
          <button
            onClick={() => {
              setAccessibilityEnabled(!accessibilityEnabled);
            }}
            className={`w-full p-3 rounded-lg flex items-center justify-between transition-colors ${
              accessibilityEnabled 
                ? 'bg-primary/10 dark:bg-primary/20 border border-primary/50 text-foreground' 
                : 'bg-muted border border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
            data-testid="web-accessibility-large-text"
          >
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              <span className="text-sm font-medium">Large Text</span>
            </div>
            <div className={`w-10 h-5 rounded-full transition-colors flex items-center ${
              accessibilityEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mx-0.5 ${
                accessibilityEnabled ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
          </button>

          {/* Info */}
          <div className="mt-3 text-xs text-muted-foreground">
            <p>Enables larger text and improved readability throughout the site.</p>
          </div>
        </div>
      )}

      {/* Enabled Indicator */}
      {accessibilityEnabled && !showPanel && (
        <div className="absolute -top-1 -left-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <span className="text-white text-[10px] font-bold">A+</span>
        </div>
      )}
    </div>
  );
}
